import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";

const INDUSTRIES = ["BFSI", "Healthcare", "ECommerce", "Manufacturing", "IT_ITES", "Education", "Generic"];
const COMMON_TZ = ["Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Europe/London", "America/New_York", "UTC"];

const ContactFields = ({ label, value, onChange, prefix, disabled }) => (
  <fieldset className="border border-slate-200 rounded-xl p-4 space-y-3">
    <legend className="px-1 text-xs font-semibold text-slate-600">{label}</legend>
    {["name", "email", "phone"].map((k) => (
      <div key={k} className="grid grid-cols-[70px_1fr] items-center gap-2">
        <Label className="text-xs capitalize text-slate-500">{k}</Label>
        <Input value={value?.[k] || ""} disabled={disabled} className="h-9"
               onChange={(e) => onChange({ ...value, [k]: e.target.value })} data-testid={`${prefix}-${k}`} />
      </div>
    ))}
  </fieldset>
);

export const OrgSettingsForm = () => {
  const auth = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const editable = auth.can("tenant_settings", "edit");

  useEffect(() => {
    api.get("/platform/tenant").then(({ data }) => setForm(data)).catch(() => {});
  }, [auth.user?.tenant_id]);

  if (!form) return <div className="text-sm text-slate-500">Loading…</div>;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/platform/tenant", {
        name: form.name, industry: form.industry || null,
        employee_count: form.employee_count ? Number(form.employee_count) : null,
        privacy_contact: form.privacy_contact, dpdp_contact: form.dpdp_contact,
        business_address: form.business_address || "", timezone: form.timezone,
        require_owner_approval_for_support: !!form.require_owner_approval_for_support,
      });
      setForm(data);
      auth.refreshMe();
      toast.success("Organization settings saved");
    } catch (e) {
      toast.error(apiError(e, "Could not save settings"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-6 max-w-4xl" data-testid="org-settings-form">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Organization name</Label>
          <Input value={form.name || ""} onChange={set("name")} disabled={!editable} data-testid="org-name-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Industry</Label>
          <Select value={form.industry || ""} onValueChange={set("industry")} disabled={!editable}>
            <SelectTrigger data-testid="org-industry-select"><SelectValue placeholder="Select industry" /></SelectTrigger>
            <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Employee count</Label>
          <Input type="number" min={1} value={form.employee_count ?? ""} onChange={set("employee_count")} disabled={!editable}
                 data-testid="org-employee-count-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Timezone</Label>
          <Select value={form.timezone} onValueChange={set("timezone")} disabled={!editable}>
            <SelectTrigger data-testid="org-timezone-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[...new Set([form.timezone, ...COMMON_TZ])].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <ContactFields label="Privacy contact" value={form.privacy_contact} onChange={set("privacy_contact")}
                       prefix="org-privacy-contact" disabled={!editable} />
        <ContactFields label="DPDP contact (DPO / Grievance Officer)" value={form.dpdp_contact} onChange={set("dpdp_contact")}
                       prefix="org-dpdp-contact" disabled={!editable} />
      </div>
      <div className="space-y-1.5">
        <Label>Business address</Label>
        <Textarea rows={3} value={form.business_address || ""} onChange={set("business_address")} disabled={!editable}
                  data-testid="org-address-input" />
      </div>
      <div className="flex items-center justify-between border border-slate-200 rounded-xl p-4">
        <div>
          <div className="text-sm font-medium text-[#003366]">Require owner approval for support access</div>
          <div className="text-xs text-slate-500">
            When on, a Platform Admin's Support Mode request must be approved by an Organization Owner before it
            becomes active.
          </div>
        </div>
        <Switch checked={!!form.require_owner_approval_for_support} disabled={!editable}
                onCheckedChange={(v) => setForm((f) => ({ ...f, require_owner_approval_for_support: v }))}
                data-testid="org-require-approval-switch" />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-mono text-slate-400" data-testid="org-tenant-id">Tenant ID · {form.id}</span>
        {editable && (
          <Button onClick={save} disabled={saving || !form.name?.trim()} className="bg-[#003366] hover:bg-[#003366]/90"
                  data-testid="org-settings-save">
            {saving ? "Saving…" : "Save settings"}
          </Button>
        )}
      </div>
    </div>
  );
};
